import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { accountingApi } from '../../../services/accountingApi';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from '../exportUtils';

const DEFAULT_ROWS = 6;
const emptyLine = () => ({ account_id: '', account_name: '', debit: '', credit: '' });
const makeLines = () => Array.from({ length: DEFAULT_ROWS }, emptyLine);

const todayStr = () => new Date().toISOString().slice(0, 10);

const JournalEntry = () => {
  const [entries, setEntries]         = useState([]);
  const [accounts, setAccounts]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState('');
  
  // Filters & Search
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [startDate, setStartDate]     = useState('');
  const [endDate, setEndDate]         = useState('');
  const [search, setSearch]           = useState('');

  // Pagination & Export
  const [perPage, setPerPage]         = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [showExport, setShowExport]   = useState(false);

  // Action Dropdown state
  const [activeActionId, setActiveActionId] = useState(null);

  // Modal State
  const [showModal, setShowModal]     = useState(false);
  const [editId, setEditId]           = useState(null);
  const [lines, setLines]             = useState(makeLines());
  const [header, setHeader]           = useState({ ref: '', date: todayStr(), notes: '' });

  const exportDropdownRef = useRef(null);
  const actionDropdownRef = useRef(null);

  const loadEntries = () => {
    setLoading(true);
    Promise.all([accountingApi.getJournalEntries(), accountingApi.getAccounts()])
      .then(([jeRes, acRes]) => {
        const toArr = r => Array.isArray(r) ? r : Array.isArray(r.data) ? r.data : Array.isArray(r.data?.data) ? r.data.data : [];
        setEntries(toArr(jeRes));
        setAccounts(toArr(acRes));
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadEntries();
  }, []);

  // Close dropdowns on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExport(false);
      }
      if (actionDropdownRef.current && !actionDropdownRef.current.contains(event.target)) {
        setActiveActionId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Calculations for Modal
  const totalDebit  = lines.reduce((s, l) => s + (parseFloat(l.debit)  || 0), 0);
  const totalCredit = lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
  const balanced    = Math.abs(totalDebit - totalCredit) < 0.001 && totalDebit > 0;

  const updateLine = (i, field, val) =>
    setLines(prev => prev.map((l, idx) => {
      if (idx !== i) return l;
      if (field === 'account_id') {
        const acc = accounts.find(a => String(a.id) === String(val));
        return { ...l, account_id: val, account_name: acc?.name || '' };
      }
      return { ...l, [field]: val };
    }));

  const openAddModal = () => {
    setEditId(null);
    setLines(makeLines());
    setHeader({ ref: '', date: todayStr(), notes: '' });
    setShowModal(true);
  };

  const openEditModal = async (entry) => {
    setActiveActionId(null);
    setLoading(true);
    try {
      const res = await accountingApi.getJournalEntry(entry.id);
      const data = res.data || res;
      setEditId(entry.id);
      setHeader({
        ref: data.ref_no || entry.ref_no || '',
        date: (data.operation_date || entry.operation_date || todayStr()).slice(0, 10),
        notes: data.note || entry.note || '',
      });

      const entryLines = (data.lines || data.entries || []).map(l => ({
        account_id: l.account_id || l.accounting_account_id,
        account_name: l.account_name || l.account?.name || '',
        debit: l.type === 'debit' ? (l.amount || l.debit || '') : '',
        credit: l.type === 'credit' ? (l.amount || l.credit || '') : '',
      }));

      while (entryLines.length < DEFAULT_ROWS) {
        entryLines.push(emptyLine());
      }
      setLines(entryLines);
      setShowModal(true);
    } catch (e) {
      toast.error(`Failed to load journal details: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const usedLines = lines.filter(l => l.account_id && (parseFloat(l.debit) > 0 || parseFloat(l.credit) > 0));
    if (!usedLines.length || !balanced) return;
    setSaving(true);
    try {
      const payload = {
        ref_no: header.ref,
        journal_date: header.date,
        transaction_date: header.date,
        note: header.notes,
        additional_notes: header.notes,
        lines: usedLines.map(l => ({
          account_id: l.account_id,
          debit:  parseFloat(l.debit)  || 0,
          credit: parseFloat(l.credit) || 0,
        })),
        entries: usedLines.map(l => ({
          account_id: l.account_id,
          debit:  parseFloat(l.debit)  || 0,
          credit: parseFloat(l.credit) || 0,
        })),
      };

      if (editId) {
        await accountingApi.updateJournalEntry(editId, payload);
        toast.success('Journal entry updated successfully!');
      } else {
        await accountingApi.createJournalEntry(payload);
        toast.success('Journal entry saved successfully!');
      }
      setShowModal(false);
      loadEntries();
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setActiveActionId(null);
    if (!window.confirm('Delete this journal entry?')) return;
    try {
      await accountingApi.deleteJournalEntry(id);
      toast.success('Journal entry deleted successfully!');
      loadEntries();
    } catch (e) {
      toast.error(`Delete failed: ${e.message}`);
    }
  };

  // Filter entries
  const filtered = entries.filter(e => {
    const dateStr = (e.operation_date || e.journal_date || e.date || '').slice(0, 10);
    if (startDate && dateStr < startDate) return false;
    if (endDate && dateStr > endDate) return false;

    const notes = (e.note || e.additional_notes || e.description || '').toLowerCase();
    const ref   = String(e.ref_no || e.ref || e.id || '').toLowerCase();
    const added = String(e.added_by || '').toLowerCase();
    const s     = search.toLowerCase();

    return notes.includes(s) || ref.includes(s) || added.includes(s);
  });

  // Pagination calculations
  const totalItems = filtered.length;
  const totalPages = Math.ceil(totalItems / perPage) || 1;
  const startIndex = (currentPage - 1) * perPage;
  const paginatedEntries = filtered.slice(startIndex, startIndex + perPage);

  const getPaginationRange = (current, total) => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const pages = [1];
    if (current > 3) pages.push('...');
    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    if (current < total - 2) pages.push('...');
    pages.push(total);
    return pages;
  };

  // Export handler matching Transactions.jsx
  const handleExport = (format) => {
    if (filtered.length === 0) {
      toast.warning('No data to export');
      setShowExport(false);
      return;
    }
    const headers = ['Action', 'Journal Date', 'Reference No', 'Added By', 'Additional notes'];
    const rows = filtered.map(e => [
      'Actions',
      e.operation_date || e.journal_date || '—',
      e.ref_no || e.ref || '—',
      e.added_by || '—',
      e.note || e.additional_notes || '—',
    ]);

    if (format === 'CSV') exportToCSV(headers, rows, 'Journal_Entries.csv');
    else if (format === 'Excel') exportToExcel(headers, rows, 'Journal_Entries.xlsx');
    else if (format === 'PDF') exportToPDF(headers, rows, 'Journal Entries Report');
    else if (format === 'Print') printPage(headers, rows, 'Journal Entries');
    setShowExport(false);
  };

  if (loading && entries.length === 0) return <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>Loading journal entries...</div>;
  if (error) return <div style={{ padding: '2rem', textAlign: 'center', color: '#ef4444' }}>Error: {error}</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      
      {/* ── Metric Summary Cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.85rem' }}>
        {[
          { label: 'Total Entries', value: entries.length, color: 'blue' },
          { label: 'Posted', value: entries.filter(e => (e.status || '').toLowerCase() === 'approved').length, color: 'green' },
          { label: 'Pending', value: entries.filter(e => (e.status || '').toLowerCase() !== 'approved').length, color: 'orange' },
          { label: 'Total Debits', value: `$${entries.reduce((s, e) => s + (parseFloat(e.debit) || 0), 0).toLocaleString()}`, color: 'purple' },
        ].map(s => (
          <div key={s.label} className={`acc-stat-card ${s.color}`} style={{ padding: '1.25rem 1.35rem', gap: '0.4rem', borderRadius: '12px', minHeight: '90px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div className="acc-stat-label" style={{ fontSize: '0.82rem', letterSpacing: '0.04em' }}>{s.label}</div>
            <div className="acc-stat-value" style={{ fontSize: '1.65rem', fontWeight: 800, lineHeight: '1.2' }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* ── Main Data Card ── */}
      <div className="table-content-card">
        
        {/* Top Header & Toolbar matching Transactions.jsx */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '1rem' }}>
          
          {/* Show N entries selector */}
          <div className="table-controls-left" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--acc-text-muted)' }}>Show</span>
            <select
              value={perPage}
              onChange={e => { setPerPage(Number(e.target.value)); setCurrentPage(1); }}
              style={{ height: '40px', padding: '0 0.75rem', border: '1px solid var(--acc-border)', borderRadius: 'var(--acc-radius-sm)', fontSize: '0.875rem', outline: 'none', background: 'var(--acc-surface)', color: 'var(--acc-text-main)', cursor: 'pointer' }}
            >
              {[10, 25, 50, 100].map(n => <option key={n}>{n}</option>)}
            </select>
            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--acc-text-muted)' }}>entries</span>
          </div>

          {/* Right: search + filter + export + add */}
          <div className="table-controls-right" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            {/* Search Input */}
            <div className="tabs-search-wrapper">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--acc-text-muted)', marginLeft: '0.5rem' }}>
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                className="tabs-search-input"
                placeholder="Search entries..."
                value={search}
                onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
              />
            </div>

            {/* Filter button */}
            <button
              className="acc-btn-secondary"
              onClick={() => setShowFilterModal(true)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ opacity: 0.8 }}><path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z"/></svg>
              Filters
            </button>

            {/* Export dropdown */}
            <div ref={exportDropdownRef} style={{ position: 'relative' }}>
              <button
                className="acc-btn-secondary"
                onClick={() => setShowExport(p => !p)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.8 }}>
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Export ▾
              </button>
              {showExport && (
                <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 0.5rem)', background: 'var(--acc-surface)', border: '1px solid var(--acc-border)', borderRadius: 'var(--acc-radius)', boxShadow: 'var(--acc-shadow-lg)', zIndex: 50, minWidth: '160px', overflow: 'hidden' }}>
                  {['CSV','Excel','PDF','Print'].map(f => (
                    <div
                      key={f}
                      onClick={() => handleExport(f)}
                      style={{ padding: '0.75rem 1rem', cursor: 'pointer', fontSize: '0.9rem', color: 'var(--acc-text-main)', borderBottom: f !== 'Print' ? '1px solid var(--acc-border)' : 'none', transition: 'background 0.2s' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--acc-bg)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      Export {f}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* + New Entry Button */}
            <button className="acc-btn-primary" onClick={openAddModal}>
              <span className="plus-icon" style={{ fontSize: '1.2rem', lineHeight: 1 }}>+</span> New Entry
            </button>

          </div>
        </div>

        {/* Active Filter Badges */}
        {(startDate || endDate) && (
          <div style={{ padding: '0.75rem 1.5rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap', borderBottom: '1px solid var(--acc-border)', background: 'var(--acc-bg)', alignItems: 'center', marginBottom: '1rem' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--acc-text-muted)' }}>Active Filters:</span>
            {startDate && (
              <span className="acc-badge acc-badge-blue">
                From: {startDate} <span style={{ cursor: 'pointer', marginLeft: '6px', fontSize: '1rem', lineHeight: 1 }} onClick={() => setStartDate('')}>×</span>
              </span>
            )}
            {endDate && (
              <span className="acc-badge acc-badge-blue">
                To: {endDate} <span style={{ cursor: 'pointer', marginLeft: '6px', fontSize: '1rem', lineHeight: 1 }} onClick={() => setEndDate('')}>×</span>
              </span>
            )}
            <button onClick={() => { setStartDate(''); setEndDate(''); }} style={{ background: 'none', border: 'none', color: 'var(--acc-danger)', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}>Clear All</button>
          </div>
        )}

        {/* Data Table */}
        <div className="acc-table-wrapper" style={{ overflowX: 'auto', width: '100%' }}>
          <table className="acc-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'center', padding: '0.75rem 1rem', width: '120px' }}>Action</th>
                <th style={{ textAlign: 'left', padding: '0.75rem 1rem' }}>Journal Date</th>
                <th style={{ textAlign: 'left', padding: '0.75rem 1rem' }}>Reference No</th>
                <th style={{ textAlign: 'left', padding: '0.75rem 1rem' }}>Added By</th>
                <th style={{ textAlign: 'left', padding: '0.75rem 1rem' }}>Additional notes</th>
              </tr>
            </thead>
            <tbody>
              {paginatedEntries.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', padding: '2.5rem', color: 'var(--acc-text-muted)' }}>
                    No journal entries found
                  </td>
                </tr>
              )}
              {paginatedEntries.map((e, idx) => (
                <tr key={e.id || idx}>
                  
                  {/* Action Column */}
                  <td style={{ padding: '0.75rem 1rem', textAlign: 'center', position: 'relative' }}>
                    <div style={{ position: 'relative', display: 'inline-block' }} ref={activeActionId === e.id ? actionDropdownRef : null}>
                      <button
                        onClick={() => setActiveActionId(activeActionId === e.id ? null : e.id)}
                        style={{
                          background: '#00c0ef', color: '#fff', border: 'none', borderRadius: '14px',
                          padding: '0.25rem 0.85rem', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
                          display: 'inline-flex', alignItems: 'center', gap: '0.35rem'
                        }}
                      >
                        Actions <span style={{ fontSize: '0.7rem' }}>▾</span>
                      </button>
                      {activeActionId === e.id && (
                        <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: '0.25rem', background: 'var(--acc-surface)', border: '1px solid var(--acc-border)', borderRadius: 'var(--acc-radius)', boxShadow: 'var(--acc-shadow-lg)', zIndex: 200, minWidth: '120px', overflow: 'hidden', textAlign: 'left' }}>
                          <button
                            onClick={() => openEditModal(e)}
                            style={{ width: '100%', padding: '0.6rem 0.85rem', background: 'none', border: 'none', textAlign: 'left', fontSize: '0.85rem', color: 'var(--acc-text-main)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                            onMouseEnter={ev => ev.currentTarget.style.background = 'var(--acc-bg)'}
                            onMouseLeave={ev => ev.currentTarget.style.background = 'none'}
                          >
                            ✏️ Edit
                          </button>
                          <button
                            onClick={() => handleDelete(e.id)}
                            style={{ width: '100%', padding: '0.6rem 0.85rem', background: 'none', border: 'none', textAlign: 'left', fontSize: '0.85rem', color: 'var(--acc-danger)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                            onMouseEnter={ev => ev.currentTarget.style.background = '#fef2f2'}
                            onMouseLeave={ev => ev.currentTarget.style.background = 'none'}
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </td>

                  {/* Journal Date */}
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: 'var(--acc-text-main)', whiteSpace: 'nowrap' }}>
                    {e.operation_date || e.journal_date || e.date || '—'}
                  </td>

                  {/* Reference No */}
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', fontWeight: 600, color: 'var(--acc-text-main)' }}>
                    {e.ref_no || e.ref || e.id || '—'}
                  </td>

                  {/* Added By */}
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: 'var(--acc-text-main)' }}>
                    {e.added_by || '—'}
                  </td>

                  {/* Additional notes */}
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: 'var(--acc-text-muted)' }}>
                    {e.note || e.additional_notes || e.description || '—'}
                  </td>

                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* DataTables Pagination Controls */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.25rem', padding: '0 0.5rem 0.5rem 0.5rem', flexWrap: 'wrap', gap: '1rem', fontSize: '0.85rem', color: '#475569' }}>
          <div>
            Showing {totalItems === 0 ? 0 : startIndex + 1} to {Math.min(startIndex + perPage, totalItems)} of {totalItems} entries
          </div>

          <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
            <button
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              style={{
                padding: '0.35rem 0.75rem',
                borderRadius: '4px',
                border: '1px solid #cbd5e1',
                background: currentPage <= 1 ? '#f1f5f9' : '#fff',
                color: currentPage <= 1 ? '#94a3b8' : '#1e293b',
                cursor: currentPage <= 1 ? 'not-allowed' : 'pointer',
                fontWeight: 600,
                fontSize: '0.85rem'
              }}
            >
              Previous
            </button>

            {getPaginationRange(currentPage, totalPages).map((p, idx) => (
              p === '...' ? (
                <span key={`ellipsis-${idx}`} style={{ padding: '0.35rem 0.5rem', color: '#94a3b8', fontSize: '0.85rem' }}>...</span>
              ) : (
                <button
                  key={p}
                  onClick={() => setCurrentPage(p)}
                  style={{
                    padding: '0.35rem 0.75rem',
                    borderRadius: '4px',
                    border: '1px solid #cbd5e1',
                    background: currentPage === p ? '#2563eb' : '#fff',
                    color: currentPage === p ? '#fff' : '#1e293b',
                    fontWeight: currentPage === p ? 700 : 500,
                    cursor: 'pointer',
                    fontSize: '0.85rem'
                  }}
                >
                  {p}
                </button>
              )
            ))}

            <button
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              style={{
                padding: '0.35rem 0.75rem',
                borderRadius: '4px',
                border: '1px solid #cbd5e1',
                background: currentPage >= totalPages ? '#f1f5f9' : '#fff',
                color: currentPage >= totalPages ? '#94a3b8' : '#1e293b',
                cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
                fontWeight: 600,
                fontSize: '0.85rem'
              }}
            >
              Next
            </button>
          </div>
        </div>

      </div>

      {/* ── Filter Modal ── */}
      {showFilterModal && (
        <div
          onClick={() => setShowFilterModal(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: 'var(--acc-surface)', borderRadius: '12px', width: '90%', maxWidth: '480px', padding: '1.5rem', boxShadow: 'var(--acc-shadow-lg)' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: 'var(--acc-text-main)' }}>Filter Journal Entries</h3>
              <button onClick={() => setShowFilterModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: 'var(--acc-text-muted)' }}>✕</button>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--acc-text-main)', marginBottom: '0.4rem' }}>Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  style={{ width: '100%', padding: '0.6rem 0.8rem', border: '1px solid var(--acc-border)', borderRadius: '6px', fontSize: '0.9rem', outline: 'none', background: 'var(--acc-surface)', color: 'var(--acc-text-main)', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--acc-text-main)', marginBottom: '0.4rem' }}>End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  style={{ width: '100%', padding: '0.6rem 0.8rem', border: '1px solid var(--acc-border)', borderRadius: '6px', fontSize: '0.9rem', outline: 'none', background: 'var(--acc-surface)', color: 'var(--acc-text-main)', boxSizing: 'border-box' }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem' }}>
              <button
                onClick={() => { setStartDate(''); setEndDate(''); setShowFilterModal(false); }}
                style={{ background: 'none', border: '1px solid var(--acc-border)', borderRadius: '6px', padding: '0.5rem 1.25rem', fontSize: '0.85rem', fontWeight: 600, color: 'var(--acc-text-main)', cursor: 'pointer' }}
              >
                Reset
              </button>
              <button
                onClick={() => setShowFilterModal(false)}
                style={{ background: 'var(--acc-primary)', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.5rem 1.5rem', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add / Edit Journal Entry Modal ── */}
      {showModal && (
        <div
          onClick={() => setShowModal(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: 'var(--acc-surface)', borderRadius: '8px', width: '95%', maxWidth: '720px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 25px 50px rgba(0,0,0,0.2)' }}
          >
            {/* Header */}
            <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--acc-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: 'var(--acc-text-main)' }}>
                {editId ? 'Edit Journal Entry' : 'Add Journal Entry'}
              </h3>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: 'var(--acc-text-muted)', lineHeight: 1 }}>✕</button>
            </div>

            {/* Body */}
            <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginBottom: '1.5rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.85rem', fontWeight: 600, color: 'var(--acc-text-main)' }}>Reference No</label>
                  <input
                    value={header.ref}
                    onChange={e => setHeader(p => ({ ...p, ref: e.target.value }))}
                    placeholder="e.g. INV-001"
                    style={{ width: '100%', padding: '0.75rem 0.875rem', border: '1px solid var(--acc-border)', background: 'var(--acc-surface)', color: 'var(--acc-text-main)', borderRadius: '6px', boxSizing: 'border-box', outline: 'none', fontSize: '0.875rem' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.85rem', fontWeight: 600, color: 'var(--acc-text-main)' }}>Journal Date <span style={{ color: 'var(--acc-danger)' }}>*</span></label>
                  <input
                    value={header.date}
                    onChange={e => setHeader(p => ({ ...p, date: e.target.value }))}
                    placeholder="YYYY-MM-DD"
                    style={{ width: '100%', padding: '0.75rem 0.875rem', border: '1px solid var(--acc-border)', background: 'var(--acc-surface)', color: 'var(--acc-text-main)', borderRadius: '6px', boxSizing: 'border-box', outline: 'none', fontSize: '0.875rem' }}
                  />
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.85rem', fontWeight: 600, color: 'var(--acc-text-main)' }}>Additional Notes</label>
                  <textarea
                    value={header.notes}
                    onChange={e => setHeader(p => ({ ...p, notes: e.target.value }))}
                    placeholder="Additional notes..."
                    rows={3}
                    style={{ width: '100%', padding: '0.75rem 0.875rem', border: '1px solid var(--acc-border)', background: 'var(--acc-surface)', color: 'var(--acc-text-main)', borderRadius: '6px', boxSizing: 'border-box', outline: 'none', resize: 'vertical', fontFamily: 'inherit', fontSize: '0.875rem' }}
                  />
                </div>
              </div>

              {/* Lines table */}
              <div style={{ width: '100%', overflowX: 'auto', border: '1px solid var(--acc-border)', borderRadius: '8px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem', background: 'var(--acc-bg)' }}>
                  <colgroup>
                    <col style={{ width: 44 }} />
                    <col />
                    <col style={{ width: '25%' }} />
                    <col style={{ width: '25%' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'center', padding: '0.85rem 0.75rem', borderBottom: '1px solid var(--acc-border)', color: 'var(--acc-text-muted)', fontWeight: 600 }}>#</th>
                      <th style={{ textAlign: 'left', padding: '0.85rem 0.75rem', borderBottom: '1px solid var(--acc-border)', color: 'var(--acc-text-muted)', fontWeight: 600 }}>Account</th>
                      <th style={{ textAlign: 'left', padding: '0.85rem 0.75rem', borderBottom: '1px solid var(--acc-border)', color: 'var(--acc-text-muted)', fontWeight: 600 }}>Debit</th>
                      <th style={{ textAlign: 'left', padding: '0.85rem 0.75rem', borderBottom: '1px solid var(--acc-border)', color: 'var(--acc-text-muted)', fontWeight: 600 }}>Credit</th>
                    </tr>
                  </thead>
                  <tbody style={{ background: 'var(--acc-surface)' }}>
                    {lines.map((l, i) => (
                      <tr key={i}>
                        <td style={{ textAlign: 'center', padding: '0.65rem 0.75rem', borderBottom: '1px solid var(--acc-border)', color: 'var(--acc-text-muted)', fontWeight: 500 }}>{i + 1}</td>
                        <td style={{ padding: '0.65rem 0.75rem', borderBottom: '1px solid var(--acc-border)' }}>
                          <select
                            value={l.account_id}
                            onChange={e => updateLine(i, 'account_id', e.target.value)}
                            style={{ width: '100%', padding: '0.65rem 0.75rem', border: '1px solid var(--acc-border)', borderRadius: '4px', boxSizing: 'border-box', outline: 'none', background: 'var(--acc-surface)', color: 'var(--acc-text-main)', fontSize: '0.85rem' }}
                          >
                            <option value="">Please select</option>
                            {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                          </select>
                        </td>
                        <td style={{ padding: '0.65rem 0.75rem', borderBottom: '1px solid var(--acc-border)' }}>
                          <input
                            type="number"
                            min="0"
                            value={l.debit}
                            onChange={e => updateLine(i, 'debit', e.target.value)}
                            placeholder="0.00"
                            style={{ width: '100%', padding: '0.65rem 0.75rem', border: '1px solid var(--acc-border)', background: 'var(--acc-surface)', color: 'var(--acc-text-main)', borderRadius: '4px', boxSizing: 'border-box', outline: 'none', fontSize: '0.85rem' }}
                          />
                        </td>
                        <td style={{ padding: '0.65rem 0.75rem', borderBottom: '1px solid var(--acc-border)' }}>
                          <input
                            type="number"
                            min="0"
                            value={l.credit}
                            onChange={e => updateLine(i, 'credit', e.target.value)}
                            placeholder="0.00"
                            style={{ width: '100%', padding: '0.65rem 0.75rem', border: '1px solid var(--acc-border)', background: 'var(--acc-surface)', color: 'var(--acc-text-main)', borderRadius: '4px', boxSizing: 'border-box', outline: 'none', fontSize: '0.85rem' }}
                          />
                        </td>
                      </tr>
                    ))}
                    {/* Total row */}
                    <tr>
                      <td colSpan={2} style={{ padding: '0.85rem 0.75rem', borderBottom: 'none', fontWeight: 700, color: 'var(--acc-text-main)' }}>Total</td>
                      <td style={{ padding: '0.85rem 0.75rem', borderBottom: 'none', fontWeight: 700, color: balanced ? 'var(--acc-success)' : totalDebit > 0 ? 'var(--acc-danger)' : 'var(--acc-text-main)' }}>
                        {totalDebit > 0 ? `$${totalDebit.toFixed(2)}` : ''}
                      </td>
                      <td style={{ padding: '0.85rem 0.75rem', borderBottom: 'none', fontWeight: 700, color: balanced ? 'var(--acc-success)' : totalCredit > 0 ? 'var(--acc-danger)' : 'var(--acc-text-main)' }}>
                        {totalCredit > 0 ? `$${totalCredit.toFixed(2)}` : ''}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {!balanced && totalDebit > 0 && (
                <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', color: '#ef4444', fontSize: '0.85rem', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  ⚠ Not balanced — Difference: ${Math.abs(totalDebit - totalCredit).toFixed(2)}
                </div>
              )}

              <button
                onClick={() => setLines(prev => [...prev, emptyLine()])}
                style={{ marginTop: '1rem', background: 'var(--acc-bg)', border: '1px dashed var(--acc-border)', borderRadius: '6px', padding: '0.75rem 1rem', cursor: 'pointer', fontSize: '0.875rem', color: 'var(--acc-text-muted)', width: '100%', fontWeight: 600, transition: 'all 0.15s ease' }}
                onMouseEnter={e => { e.target.style.borderColor = 'var(--acc-text-muted)'; e.target.style.color = 'var(--acc-text-main)'; e.target.style.background = 'var(--acc-primary-light)'; }}
                onMouseLeave={e => { e.target.style.borderColor = 'var(--acc-border)'; e.target.style.color = 'var(--acc-text-muted)'; e.target.style.background = 'var(--acc-bg)'; }}
              >
                + Add Row
              </button>
            </div>

            {/* Footer */}
            <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--acc-border)', display: 'flex', justifyContent: 'center', gap: '0.75rem', background: 'var(--acc-bg)' }}>
              <button
                onClick={handleSave}
                disabled={saving || !balanced}
                style={{ background: saving || !balanced ? '#a5b4fc' : '#5b21b6', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.65rem 2.5rem', fontSize: '0.875rem', fontWeight: 600, cursor: saving || !balanced ? 'not-allowed' : 'pointer', transition: 'background 0.15s ease' }}
                onMouseEnter={e => { if (!saving && balanced) e.target.style.background = '#4c1d95'; }}
                onMouseLeave={e => { if (!saving && balanced) e.target.style.background = '#5b21b6'; }}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button
                onClick={() => setShowModal(false)}
                style={{ background: '#334155', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.65rem 2.5rem', fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer', transition: 'background 0.15s ease' }}
                onMouseEnter={e => e.target.style.background = '#1e293b'}
                onMouseLeave={e => e.target.style.background = '#334155'}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JournalEntry;
